<template>
    <!-- START TEST: PPIWEBM-2699 Essential plan prioritization on Pick a plan -->
    <div id="pick-a-plan" :class="getPickAPlanWrapperClassList">
        <!-- END TEST: PPIWEBM-2699 Essential plan prioritization on Pick a plan -->
        <section class="padded-container">
            <div class="pick-a-plan-grid" aa-region="pick a plan">
                <div :class="getGridClass">
                    <steps-header v-if="!hasSkipExplainers" class="qt-PAPheader" />
                    <h1
                        v-else-if="dspHeading"
                        class="view-heading"
                        :tabindex="pageHeadingTabIndex"
                        ref="pageHeading"
                        @blur="removeHeadingTabIndex">
                        {{ getHeading }}
                    </h1>

                    <div :class="subHeadingClass">
                        {{ subHeadingCopy }}
                    </div>

                    <div v-if="displayAnnualCheckbox" class="annual-plan-toggle-wrapper">
                        <FormToggleButton
                            :toggleLabelHTML="annualCheckboxLabelCopy"
                            :defaultActive="annualToggleDefaultState"
                            :toggleLeftLabel="getToggleLeftLabel"
                            :toggleRightLabel="getToggleRightLabel"
                            @change="handleCheckboxChange"></FormToggleButton>

                        <!-- START TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy -->
                        <AnnualToggleCenterAlignedGreenSavingsPill
                            :defaultActive="annualToggleDefaultState"
                            :toggleLeftLabel="getToggleLeftLabel"
                            :toggleRightLabel="getToggleRightLabel"
                            @change="
                                handleCheckboxChange
                            "></AnnualToggleCenterAlignedGreenSavingsPill>

                        <ToggleButton
                            :leftCopy="getToggleLeftLabel.trim()"
                            :rightCopy="getToggleRightLabel.trim()"
                            toggleClass="toggle-button-wrapper"
                            :defaultActive="annualToggleDefaultState"
                            @toggle="handleToggle"></ToggleButton>

                        <!-- END TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy -->
                    </div>

                    <PlanGroupToggle
                        v-if="planGroupToggle"
                        :toggle="planGroupToggle"
                        :active="planGroupToggleSide"
                        @change="onPlanGroupToggle"></PlanGroupToggle>

                    <div :class="plansGridClass">
                        <PickAPlanBox
                            v-for="(plan, idx) in plans"
                            :key="idx"
                            :idx="idx"
                            :plan="plan"
                            :isActivePlan="activePlan === idx"
                            :isNoFreeTrialCampaign="isNoFreeTrialCampaign"
                            :hasSingleTrialPlan="hasSingleTrialPlan"
                            :hasSingleCouponPlan="hasSingleCouponPlan"
                            :planValuePillCopy="planValuePillCopy"
                            :ftcCompliancePickAPlanEnabled="ftcCompliancePickAPlanEnabled"
                            :monthly-only="monthlyOnly"
                            :isEssentialPlanPrioritizationVariant="
                                isEssentialPlanPrioritizationVariant
                            "
                            @planSelected="handlePlanSelected"
                            @handleCardSelected="handleCardSelected"></PickAPlanBox>
                    </div>

                    <!-- START TEST PLOCTOPLUS-2036 Pick a Plan: Plan Tiles Above Fold -->
                    <AbTestNativeAppViewPlans
                        v-if="isNativeAppView"
                        :plans="plans"
                        @planSelected="handlePlanSelected" />
                    <AbTestIntlLikePlans
                        v-if="isIntlLikeView"
                        :plans="plans"
                        @planSelected="handlePlanSelected" />
                    <!-- END TEST PLOCTOPLUS-2036 Pick a Plan: Plan Tiles Above Fold -->
                </div>

                <div class="pick-a-plan-grid-item" v-if="plansWithForeverCoupon">
                    <div class="forever-coupon-disclaimer" v-html="foreverCouponDisclaimer"></div>
                </div>
            </div>
            <div class="pick-a-plan-grid" v-if="getPickAPlanDisclaimer">
                <div :class="[getGridClass, 'pick-a-plan-disclaimers-grid']">
                    <PickAPlanDisclaimer
                        :className="getDisclaimerClass"
                        :disclaimer="getPickAPlanDisclaimer" />
                </div>
            </div>

            <div class="pick-a-plan-grid" v-if="monthlyOnly">
                <div class="pick-a-plan-continue-cta">
                    <ButtonCta :buttonConfig="getButtonConfig"></ButtonCta>
                </div>
            </div>
        </section>

        <ShowtimeBundle
            v-if="!hideBundleToggle"
            :bundleShowtime="displayBundlePlans"
            @toggleBundle="onToggleBundle"></ShowtimeBundle>

        <PartnerBundleCTA v-if="displayPartnerBundleCTA"></PartnerBundleCTA>

        <!-- Ensure the BillingCadenceModal component is always present so committing the store flag opens it -->
        <BillingCadenceModal :monthly-only="monthlyOnly" @handleSubmit="handleSubmit" />
    </div>
</template>

<script>
    import { mapActions, mapState, mapGetters } from 'vuex';
    import StepsHeader from 'aa/vue/components/StepsHeader';
    import FormCheckbox from 'atoms/CheckboxElm';
    import PickAPlanBox from 'aa/vue/components/PickAPlanBox';
    import Product from 'aa/vue/models/Product';
    import { PickAPlanConfig } from 'aa/vue/FormConfig';
    import { ACTION_NAMES, tracking } from 'services/Tracking';
    import FormToggleButton from 'aa/vue/components/FormToggleButton';
    import planGroupMixin from 'aa/vue/plugins/planGroupMixin';
    // START TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy
    import AnnualToggleCenterAlignedGreenSavingsPill from 'aa/vue/abTestRelated/AnnualToggleCenterAlignedGreenSavingsPill';
    import BillingCadenceModal from 'aa/vue/components/BillingCadenceModal';
    import ToggleButton from 'atoms/ToggleButton';
    // END TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy
    import ShowtimeBundle from 'aa/vue/components/ShowtimeBundle';
    import Breakpoints from 'helpers/breakpoints';
    import Feature, {
        CAMPAIGN_SERVICE_ENABLED,
        SKIP_EXPLAINER_STEPS,
        PLAN_HEADER_COPY_CHANGE,
        FTC_COMPLIANCE_PICKAPLAN_ENABLED,
        PICK_PLAN_TIER_PRESELECTION_ENABLED,
    } from 'aa/helpers/featureConstants';
    // START TEST PLOCTOPLUS-2036 Pick a Plan: Plan Tiles Above Fold
    import AbTestNativeAppViewPlans from 'aa/vue/components/AbTestNativeAppViewPlans';
    import AbTestIntlLikePlans from 'aa/vue/components/AbTestIntlLikePlans';
    // END TEST PLOCTOPLUS-2036 Pick a Plan: Plan Tiles Above Fold
    import PartnerBundleCTA from 'aa/vue/components/PartnerBundleCTA';
    import accesabilityMixins from 'aa/vue/plugins/account/accesabilityMixins';
    import aarpMixin from 'aa/vue/plugins/aarpMixin';
    import ButtonCta from 'atoms/ButtonCta';
    import { COUPON_TYPES } from 'aa/vue/models/CouponDiscount';
    import PickAPlanDisclaimer from './PickAPlanDisclaimer.vue';

    export default {
        name: 'PickAPlan',
        mixins: [planGroupMixin, accesabilityMixins, aarpMixin],
        data: function () {
            return {
                pricing: null,
                paymentForm: 'paymentForm',
                papConfig: PickAPlanConfig,
                buttonConfig: {},
                checkboxConfig: {},
                breakpoints: new Breakpoints(),
                enteredStepWithBundle: this.$store.getters.bundleShowtime,
                displayBundlePlans: this.$store.getters.bundleShowtime,

                // START TEST PLOCTOPLUS-2036 Pick a Plan: Plan Tiles Above Fold
                isNativeAppView: this.$store.getters['variant/isPlanTilesAboveFoldNativeAppView'],
                isIntlLikeView: this.$store.getters['variant/isPlanTilesAboveFoldIntlLikeView'],
                // END TEST PLOCTOPLUS-2036 Pick a Plan: Plan Tiles Above Fold

                addOnCode: Product.ADDON_PARTNER_BUNDLE,
                hasSingleTrialPlan: false,
                hasSingleCouponPlan: false,
                isBillingCadenceModal: this.$store.getters['variant/isBillingCadenceModal'],
                // START TEST: PPIWEBM-2699 Essential plan prioritization on Pick a plan
                isEssentialPlanPrioritizationVariant: false,
                isLoadingEssentialPlanPrioritizationVariant: false,
                // END TEST: PPIWEBM-2699 Essential plan prioritization on Pick a plan
            };
        },
        computed: {
            ...mapGetters({
                featureFlagIsActive: 'featureFlags/isActive',
            }),
            ...mapGetters('user', ['isNoTrialUser']),
            // START TEST: PPIWEBM-2699 Essential plan prioritization
            ...mapGetters('variant', ['isEssentialPlanPrioritizationVariant']),
            // END TEST: PPIWEBM-2699 Essential plan prioritization

            billingCadenceLowercase() {
                if (typeof this.billingCadence !== 'string') {
                    return '';
                }

                return this.billingCadence.toLowerCase();
            },

            getPickAPlanWrapperClassList() {
                return {
                    'monthly-only': this.monthlyOnly,
                };
            },

            getGridClass() {
                return {
                    'pick-a-plan-grid-item': true,
                };
            },

            hasSkipExplainers() {
                return this.featureIsActive(SKIP_EXPLAINER_STEPS);
            },

            dspHeading() {
                return this.hasSkipExplainers && this.getHeading;
            },
            getHeading() {
                if (this.useFreeTrialHeader && this.isTabletOrDesktop) {
                    return this.pageAttributes?.aa_page_header_free_trial ?? '';
                }
                return this.pageAttributes.aa_page_header;
            },
            isTabletOrDesktop() {
                return (
                    !this.breakpoints.isMobilePortrait() && !this.breakpoints.isMobileLandscape()
                );
            },
            monthlyPlansHaveValidCoupon() {
                const monthlyPlansWithValidCoupon = this.monthlyPlans.filter((plan) =>
                    plan.isCouponValidAndAllowed(),
                );

                return (
                    this.monthlyPlans.length > 0 &&
                    this.monthlyPlans.length === monthlyPlansWithValidCoupon.length
                );
            },
            annualPlansHaveValidCoupon() {
                const annualPlansWithValidCoupon = this.annualPlans.filter((plan) =>
                    plan.isCouponValidAndAllowed(),
                );

                return (
                    this.annualPlans.length > 0 &&
                    this.annualPlans.length === annualPlansWithValidCoupon.length
                );
            },
            allPlansAreFreeTrial() {
                const trialEnabled = this.plans.filter((plan) => plan.trial === true);
                // * Requirement was to make sure trialEnabled plans length should be same like default plans, then we will show updated header copy
                // For use cases where one of more plans are non-trial eligible, the old copy will be displayed
                return trialEnabled.length === this.plans.length;
            },
            allPlansHaveEFTCoupon() {
                const eftEnabled = this.plans.filter(
                    (plan) => plan.getCouponType() === COUPON_TYPES.FREE_TRIAL,
                );
                // * Requirement was to make sure eftEnabled plans length should be same like default plans, then we will show updated header copy
                // For use cases where one of more plans are non-trial eligible, the old copy will be displayed
                return eftEnabled.length === this.plans.length;
            },
            useFreeTrialHeader() {
                if (Feature.enabled(PLAN_HEADER_COPY_CHANGE) && !this.isGiftFlow) {
                    if (
                        !this.isNoTrialUser &&
                        (this.allPlansAreFreeTrial || this.allPlansHaveEFTCoupon)
                    ) {
                        return true;
                    } else if (this.isNoTrialUser && this.allPlansHaveEFTCoupon) {
                        return true;
                    }
                }
                return false;
            },
            recurly() {
                return this.$store.state.payment.recurly;
            },

            plans() {
                if (this.displayPartnerPlans) {
                    return this.getSortedPlans(this.plansForPartnerBundle);
                }

                if (Feature.enabled(CAMPAIGN_SERVICE_ENABLED)) {
                    const plans = this.$store.getters['plan/getPlans'].filter(this.isPickerPlan);
                    return this.getSortedPlans(plans);
                } else {
                    if (this.plansWithTierSuppression) {
                        return this.getSortedPlans(this.plansWithTierSuppression);
                    }

                    const plans = this.$store.getters['plan/getPlans'].filter(this.isPickerPlan);
                    return this.getSortedPlans(plans);
                }
            },

            /** the plans of the displayed cadence, for the plan-group toggle */
            planGroupPlans() {
                return this.$store.getters['plan/getPlans'];
            },

            planGroupStorageKey() {
                return 'pickPlanGroup';
            },

            plansWithTierSuppression() {
                if (this.isCampaign) {
                    let suppressionTier = null;

                    if (this.pageAttributes.hide_essential_plan) {
                        suppressionTier = Product.PACKAGE_CODE.LCP;
                    } else if (this.pageAttributes.hide_premium_plan) {
                        suppressionTier = Product.PACKAGE_CODE.CF;
                    }

                    if (suppressionTier !== null) {
                        return this.$store.getters['plan/getPlans'].filter((plan) => {
                            return (
                                plan.package_code !== suppressionTier &&
                                plan.trial !== this.isNoFreeTrialCampaign &&
                                !plan.isBundlePlan &&
                                this.matchesPlanGroup(plan)
                            );
                        });
                    }
                }

                return null;
            },

            plansForPartnerBundle() {
                if (Feature.enabled(CAMPAIGN_SERVICE_ENABLED)) {
                    return this.$store.getters['plan/getPlans'].filter(
                        (plan) => !plan.isBundlePlan && this.matchesPlanGroup(plan),
                    );
                } else {
                    return this.$store.getters['plan/getPlans'].filter(
                        (plan) =>
                            plan.hasAddOnCode(this.addOnCode) &&
                            plan.trial !== this.isNoFreeTrialCampaign &&
                            this.matchesPlanGroup(plan),
                    );
                }
            },

            planValuePillCopy() {
                // START TEST: PPIWEBM-2699 Essential plan prioritization
                if (this.isEssentialPlanPrioritizationVariant) {
                    return (
                        this.pageAttributes?.plan_pill_copy ?? this.$getLocale('best_value') ?? ''
                    );
                }
                // END TEST: PPIWEBM-2699 Essential plan prioritization
                const baseCopy = this.pageAttributes?.plan_pill_copy ?? '';

                return (
                    this.pageAttributes[`plan_pill_copy_${this.billingCadenceLowercase}`] ||
                    baseCopy
                );
            },

            subHeadingClass() {
                const classList = ['plan-disclaimer'];

                if (!this.isGiftFlow) {
                    classList.push('qt-cancelcopy');
                }

                return classList;
            },

            subHeadingCopy() {
                if (this.isGiftFlow) {
                    return this.giftSubHeading;
                }

                const baseCopy = this.isExSubscriber
                    ? this.pageAttributes.aa_page_sub_header_exsub
                    : this.pageAttributes.aa_page_sub_header;

                return (
                    this.pageAttributes[`aa_pap_sub_header_${this.billingCadenceLowercase}`] ||
                    baseCopy
                );
            },

            giftSubHeading: function () {
                let price = window.sessionStorage.getItem('gift_amount'),
                    formattedPrice = price ? '$' + Number(price).toFixed(2) : '';

                return this.pageAttributes.aa_pap_gift_sub_heading_format.replace(
                    '%GIFT_AMOUNT%',
                    formattedPrice,
                );
            },

            annualToggleDefaultState() {
                //set default annual plan state for toggle
                return this.annualPlanFilterIsOn
                    ? this.getToggleRightLabel
                    : this.getToggleLeftLabel;
            },

            getToggleLeftLabel() {
                return Product.TYPE_MONTHLY.toUpperCase();
            },

            getToggleRightLabel() {
                return Product.TYPE_ANNUAL.toUpperCase();
            },

            displayAnnualCheckbox() {
                return !this.isGiftFlow && !this.hideAnnualToggle && !this.monthlyOnly;
            },

            hideAnnualToggle() {
                return this.isCampaign && this.pageAttributes.hide_monthly_annual_toggle;
            },

            hideBundleToggle() {
                if (this.enteredStepWithBundle) {
                    return this.$store.getters['pageAttributes/hideBasePlanCTAs'];
                }

                return !this.displayBundlePlans;
            },

            displayPartnerBundleCTA() {
                return this.$store.getters['isPartnerBundle'];
            },

            displayPartnerPlans() {
                return this.$store.getters['isPartnerBundle'] && this.plansForPartnerBundle;
            },

            annualCheckboxLabelCopy() {
                try {
                    let format = this.pageAttributes.aa_pap_annual_plan_checkbox_format;

                    const baseCopy = format
                        .split(/(%[\w_]+%)/)
                        .map((part) => {
                            switch (part) {
                                case '%LCP_ANNUAL_PLAN_MONTHLY_PRICE_WITH_UNIT%':
                                    return this.lcpAnnualPlan?.annualPlanMonthlyPriceWithUnit();
                                case '%CF_ANNUAL_PLAN_MONTHLY_PRICE_WITH_UNIT%':
                                    return this.cfAnnualPlan?.annualPlanMonthlyPriceWithUnit();
                                default:
                                    return part;
                            }
                        })
                        .join('');

                    return (
                        this.pageAttributes[
                            `aa_pap_billing_cadence_toggle_copy_${this.billingCadenceLowercase}`
                        ] || baseCopy
                    );
                } catch (e) {
                    return '';
                }
            },

            isLoggedIn: function () {
                return this.$store.state.user.isLoggedIn;
            },

            plansGridClass() {
                let classList;

                classList = `plan-list-grid${this.plans.length === 1 ? ' single-col' : ''}`;

                return classList;
            },

            /**
             * Returns true if one or more plans has a forever coupon.
             *
             * @returns {Boolean}
             */
            plansWithForeverCoupon() {
                const hasForeverCoupon = Boolean(
                    this.plans.filter((plan) => plan?.config?.recurlyCoupon?.duration === 'forever')
                        .length,
                );

                const isAarpAndFTC =
                    this.hasValidAarpCoupon() && this.ftcCompliancePickAPlanEnabled;

                return hasForeverCoupon && !isAarpAndFTC;
            },

            foreverCouponDisclaimer() {
                return this.pageAttributes.aa_forever_coupon_disclaimer;
            },

            ...mapGetters({
                annualPlanFilterIsOn: 'plan/annualPlanFilterIsOn',
                annualPlans: 'plan/annualPlans',
                monthlyPlans: 'plan/monthlyPlans',
                bundleShowtime: 'bundleShowtime',
                cfAnnualPlan: 'plan/cfAnnualPlan',
                selectedPlan: 'plan/getSelectedPlan',
                getNextStep: 'flow/getNextStep',
                isCampaign: 'flow/isCampaign',
                isEdu: 'flow/isEdu',
                isExSubscriber: 'user/isExSubscriber',
                isGiftFlow: 'flow/isGift',
                lcpAnnualPlan: 'plan/lcpAnnualPlan',
                featureIsActive: 'featureIsActive',
                isNoFreeTrialCampaign: 'flow/isNoFreeTrialCampaign',
                // START TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy
                billingCadenceModalOpen: 'plan/getBillingCadenceModalOpen',
                annualSelectedAndMonthlyAvailable: 'plan/annualSelectedAndMonthlyAvailable',
                // END TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy
                isPickPlanTierPreselectionEnabled: 'featureFlags/isPickPlanTierPreselectionEnabled',
            }),
            ...mapState('payment', {
                recurly: (state) => state.recurly,
            }),
            ...mapState('pageAttributes', {
                pageAttributes: (state) => state.attributes,
            }),
            // Restore user and promo mapState that were removed
            ...mapState(['user']),
            ...mapState({
                promo: (state) => state.promo,
            }),
            monthlyOnly() {
                return (
                    this.featureFlagIsActive(PICK_PLAN_TIER_PRESELECTION_ENABLED) &&
                    !this.isGiftFlow
                );
            },
            ...mapState('plan', {
                allPlans: (state) => state.plans,
                activePlan: (state) => state.activePlan,
                billingCadence: (state) => state.planType,
                selectedPlanInfo: (state) => state.selectedPlanInfo,
            }),
            getButtonConfig() {
                const displayText =
                    this.selectedPlanInfo?.ctaText ||
                    this.pageAttributes?.aa_pap_button_text ||
                    'Continue';
                return {
                    displayText,
                    aaLink: 'pick a plan||continue|||||||',
                    ariaLabel: this.selectedPlanInfo?.plan?.planTitle ?? displayText,
                    href: 'javascript:void(0)',
                    styleClass: 'qt-continuebtn button',
                    tabIndex: 0,
                    event: () => {
                        const plan = this.selectedPlanInfo?.plan;
                        if (!plan) {
                            return;
                        }
                        this.handlePlanSelected(
                            plan,
                            this.selectedPlanInfo?.ctaText ?? displayText,
                        );
                    },
                };
            },
            getDisclaimerClass() {
                if (this.plansWithForeverCoupon) {
                    return [
                        'pick-a-plan-disclaimers',
                        'pick-a-plan-disclaimers-under-forever-coupon',
                    ];
                }
                return 'pick-a-plan-disclaimers';
            },
            ftcCompliancePickAPlanEnabled() {
                return this.featureFlagIsActive(FTC_COMPLIANCE_PICKAPLAN_ENABLED);
            },
            getPickAPlanDisclaimer() {
                const disclaimers = [];

                if (this.allPlansHaveEFTCoupon) {
                    disclaimers.push(this.pageAttributes.aa_pap_extended_free_trial_disclaimer);
                }

                const signUpDisclaimer = this.pageAttributes?.aa_sign_up_disclaimer;

                if (signUpDisclaimer) {
                    disclaimers.push(signUpDisclaimer);
                }

                const aarpDisclaimer =
                    this.hasValidAarpCoupon() &&
                    this.ftcCompliancePickAPlanEnabled &&
                    this.pageAttributes?.aa_sign_up_aarp_disclaimer;

                if (aarpDisclaimer) {
                    disclaimers.push(aarpDisclaimer);
                }

                return disclaimers.length ? disclaimers.join('') : null;
            },
        },
        components: {
            PartnerBundleCTA,
            StepsHeader,
            FormCheckbox,
            PickAPlanBox,
            PickAPlanDisclaimer,
            FormToggleButton,
            // START TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy
            AnnualToggleCenterAlignedGreenSavingsPill,
            BillingCadenceModal,
            ToggleButton,
            // END TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy
            ShowtimeBundle,
            // START TEST PLOCTOPLUS-2036 Pick a Plan: Plan Tiles Above Fold
            AbTestNativeAppViewPlans,
            AbTestIntlLikePlans,
            // END TEST PLOCTOPLUS-2036 Pick a Plan: Plan Tiles Above Fold
            ButtonCta,
        },
        methods: {
            ...mapActions('plan', ['updateSelectedPlan', 'filterPlans']),
            // START TEST: PPIWEBM-2699 Essential plan prioritization
            ...mapActions('variant', ['loadVariantsXhr']),
            // END TEST: PPIWEBM-2699 Essential plan prioritization
            handlePlanSelected: function (plan, ctaText) {
                this.updateSelectedPlan(plan);

                const resolvedCtaText = ctaText ?? this.getButtonConfig?.displayText ?? '';

                // track action on plan selected and showtime bundle plan selected
                this.trackSelectPlanBtn(plan, resolvedCtaText);

                if (this.monthlyOnly) {
                    this.$store.commit('plan/setBillingCadenceModalOpen', true);

                    const trackingParams = {
                        pickPlanSku: plan.recurlyCode,
                        pickPlanSelection: '1',
                        purchaseProductName: plan.titleForTracking,
                        pickPlanType: plan.planTitle,
                    };

                    tracking.trackAction(ACTION_NAMES.TRACK_PICK_PLAN_MODAL_VIEW, trackingParams);
                    return;
                }
                this.handleSubmit();
            },
            handleCheckboxChange: function (e) {
                // update value to boolean based on active toggle switch
                if (typeof e.value !== 'boolean') {
                    e.value = e.value === 'ANNUAL';
                }

                let selectedPlanType = e.value ? Product.TYPE_ANNUAL : Product.TYPE_MONTHLY;

                // reset promo/coupon on plan toggle
                if (!this.hasValidAarpCoupon()) {
                    this.$store.commit('setCampaignOfferCoupon', true);
                }

                // track toggle monthly or annual
                this.trackSelectPlanToggle(e, selectedPlanType);

                // update store with selected plan type
                this.filterPlans({
                    planType: selectedPlanType,
                });
            },
            // START TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy
            handleToggle(action) {
                const selectedPlanType = action ? Product.TYPE_ANNUAL : Product.TYPE_MONTHLY;
                this.filterPlans({
                    planType: selectedPlanType,
                });

                const trackingParams = {
                    pickPlanIsAnnual: action ? 'true' : 'false',
                };

                tracking.trackAction('trackPickPlanToggle', trackingParams);
            },
            // END TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy
            handleSubmit: function () {
                // TODO: validation before moving on
                this.$router.push(this.getNextStep);
            },

            onToggleBundle() {
                this.displayBundlePlans = !this.displayBundlePlans;

                if (!this.displayBundlePlans) {
                    this.$store.dispatch('flow/skipNextBundleStep', true);
                }
            },

            updateAnnualCheckboxPlanBundlePricingInfo() {
                const displayAnnualToggle = this.displayAnnualCheckbox;

                // if annual plan filter is on, updatePricingInfo taken care of by PickAPlanBox child component
                if (displayAnnualToggle && this.displayBundlePlans && !this.annualPlanFilterIsOn) {
                    this.annualPlans.forEach((plan) => {
                        this.$store.dispatch('plan/updatePricingInfo', {
                            plan,
                        });
                    });
                }

                this.applyPromoToAnnualPlansWhenMonthlyOnly();
            },

            /**
             * In monthlyOnly we only render monthly plan boxes, so annual plans never get
             * updatePriceAndCouponCopy from PickAPlanBox. Apply the same promo to annual
             * plans here so the Billing Cadence Modal shows promo on both options.
             */
            applyPromoToAnnualPlansWhenMonthlyOnly() {
                if (
                    !this.monthlyOnly ||
                    !this.recurly ||
                    !this.$store.state.promo ||
                    !this.annualPlans?.length
                ) {
                    return;
                }
                this.annualPlans.forEach((plan) => {
                    this.$store.dispatch('plan/updatePricingInfo', {
                        plan,
                        coupon: this.$store.state.promo,
                    });
                });
            },

            // this handle when app is navigating between components in vue router.
            setShowtimeBundleFooterPadding() {
                try {
                    let showtimBundleFooterPaddingThreshold = this.breakpoints.getMin('desktop');
                    if (window.innerWidth <= showtimBundleFooterPaddingThreshold) {
                        this.addShowtimeBundleFooterPadding();
                    } else {
                        this.removeShowtimeBundleFooterPadding();
                    }
                } catch (e) {}
            },

            addShowtimeBundleFooterPadding() {
                let footer = document.getElementsByClassName('global-footer')[0];
                if (footer !== undefined) {
                    footer.classList.add('showtime-bundle-footer');
                }
            },

            removeShowtimeBundleFooterPadding() {
                let footer = document.getElementsByClassName('global-footer')[0];
                if (footer !== undefined) {
                    footer.classList.remove('showtime-bundle-footer');
                }
            },

            trackSelectPlanBtn(plan, ctaText) {
                let trackSelectAction = this.displayBundlePlans
                    ? 'trackSelectShowtimeBundle'
                    : 'trackPickPlanSelection';

                let trackingParams = {
                    pickPlanCTA: 'select_plan',
                    pickPlanSelection: '1',
                    ctaText: ctaText,
                };

                Object.assign(trackingParams, this.$store.getters['getProductParamsForTracking']);

                tracking.trackAction(trackSelectAction, trackingParams);
            },

            trackSelectPlanToggle(e, selectedPlanType) {
                //track if user has checked annual option
                if (e.origin !== 'onCreated') {
                    let trackToggleAction = this.displayBundlePlans
                        ? 'trackPickPlanShowtimeBundleToggle'
                        : 'trackPickPlanToggle';

                    const trackingToggleParams = {
                        pickPlanIsAnnual:
                            selectedPlanType === Product.TYPE_ANNUAL ? 'true' : 'false',
                    };

                    tracking.trackAction(trackToggleAction, trackingToggleParams);
                }
            },

            updateHasSingleTrialPlan(plans) {
                const trialPlans = plans.filter((plan) => plan.trial === true);
                this.hasSingleTrialPlan = trialPlans.length === 1;
            },

            updateHasSingleCouponPlan(plans) {
                const couponPlans = plans.filter((plan) => !!plan.couponDiscount);
                this.hasSingleCouponPlan = couponPlans.length === 1;
            },

            handleCardSelected({ idx, ctaText, plan }) {
                this.$store.commit('plan/setActivePlan', idx);
                this.$store.commit('plan/setSelectedPlanInfo', { ctaText, plan });
                this.updateSelectedPlan(plan);
            },
            defaultMonthlyOnlyCtaText() {
                return this.pageAttributes?.aa_pap_button_text ?? '';
            },
            /**
             * monthlyOnly: PAP always shows monthly tiles; reset cadence filter and store selection
             * when the user returns from Payment with an annual plan still selected (CBSCOMSUB-10532).
             */
            alignMonthlyOnlyPlanTypeAndSelection() {
                if (!this.monthlyOnly) {
                    return;
                }
                this.filterPlans({
                    planType: Product.TYPE_MONTHLY,
                });
                const sel = this.selectedPlan;
                if (sel && sel.planType === Product.TYPE_ANNUAL) {
                    const monthlyMatch = this.annualSelectedAndMonthlyAvailable?.[0];
                    if (monthlyMatch) {
                        this.updateSelectedPlan(monthlyMatch);
                        const activeIndex = (this.plans || []).findIndex(
                            (p) => p.recurlyCode === monthlyMatch.recurlyCode,
                        );
                        this.$store.commit(
                            'plan/setActivePlan',
                            activeIndex >= 0 ? activeIndex : 0,
                        );
                        this.$store.commit('plan/setSelectedPlanInfo', {
                            ctaText:
                                this.selectedPlanInfo?.ctaText || this.defaultMonthlyOnlyCtaText(),
                            plan: monthlyMatch,
                        });
                    }
                }
            },

            /**
             * monthlyOnly: pre-select the first visible monthly plan unless one of them is selected
             * already, on page load and when the plan-group toggle hides the selected plan.
             */
            preselectMonthlyPlan() {
                if (!this.monthlyOnly) {
                    return;
                }
                const monthlyPlans = (this.plans || []).filter((p) => !p.isAnnual);
                const selected = this.selectedPlanInfo?.plan;
                if (
                    !monthlyPlans.length ||
                    monthlyPlans.some((p) => p.recurlyCode === selected?.recurlyCode)
                ) {
                    return;
                }
                const planToSelect = monthlyPlans[0];
                this.updateSelectedPlan(planToSelect);
                this.$store.commit('plan/setActivePlan', this.plans.indexOf(planToSelect));
                this.$store.commit('plan/setSelectedPlanInfo', {
                    ctaText: this.defaultMonthlyOnlyCtaText(),
                    plan: planToSelect,
                });
            },

            /** page attribute lookup for the plan-group toggle copy */
            planGroupCopy(key) {
                return (this.pageAttributes || {})[key];
            },

            /**
             * Showtime bundle plans are handled by the Showtime add-on flow; the group toggle picks
             * between the plain plans and the plans of the selected group.
             */
            isPickerPlan(plan) {
                return !plan.isBundlePlan && this.matchesPlanGroup(plan);
            },

            getSortedPlans(plans) {
                if (!plans) {
                    return [];
                }
                // START TEST: PPIWEBM-2699 Essential plan prioritization
                // Variant: Essential plan first (ascending by tier)
                if (this.isEssentialPlanPrioritizationVariant && plans.length > 1) {
                    return [...plans].sort((a, b) => Number(a.tier) - Number(b.tier));
                }
                // END TEST: PPIWEBM-2699 Essential plan prioritization
                // Making plans descending order by tier, as per requirements default will be descending
                // CMS user can override this by checking a checkbox(Plans Order Ascending) in the CMS
                if (!this.pageAttributes?.aa_pap_plans_order_ascending && plans.length > 1) {
                    return plans.sort((a, b) => Number(b.tier) - Number(a.tier));
                }
                return plans;
            },
            updatePricingInfo() {
                if (this.isPickPlanTierPreselectionEnabled) {
                    this.allPlans.forEach((plan) => {
                        this.$store.dispatch('plan/updatePricingInfo', {
                            plan,
                            coupon: plan.offerCouponCode,
                        });
                    });
                } else {
                    this.updateAnnualCheckboxPlanBundlePricingInfo();
                }
            },
        },

        async created() {
            this.$store.dispatch('flow/skipNextBundleStep', false);

            this.alignMonthlyOnlyPlanTypeAndSelection();

            if (this.recurly) {
                this.updatePricingInfo();
            }

            this.updateHasSingleTrialPlan(this.plans);
            this.updateHasSingleCouponPlan(this.plans);

            this.preselectMonthlyPlan();
        },

        mounted() {
            this.setShowtimeBundleFooterPadding();
        },

        activated() {
            this.alignMonthlyOnlyPlanTypeAndSelection();
        },

        destroyed() {
            this.removeShowtimeBundleFooterPadding();

            const billingCadenceOverlay = document.getElementById('billing-cadence-modal-overlay');
            const billingCadenceModal = document.getElementById('billing-cadence-modal');

            if (billingCadenceOverlay) {
                billingCadenceOverlay.remove();
            }

            if (billingCadenceModal) {
                billingCadenceModal.remove();
            }
        },

        watch: {
            recurly() {
                this.updatePricingInfo();
            },
            displayBundlePlans() {
                this.updatePricingInfo();
            },
            activePlanGroup() {
                this.preselectMonthlyPlan();
            },

            plans: {
                handler(newPlans) {
                    this.updateHasSingleTrialPlan(newPlans);
                    this.updateHasSingleCouponPlan(newPlans);
                },
                deep: true,
            },
        },
    };
</script>
